import { FC, useEffect, useMemo } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Save,
  Cancel,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledAutocomplete from "../../components/controlledComponents/ControlledAutocomplete";
import {
  TScoringStructure,
  TAssessmentComponent,
} from "../../../apis/assessment/assessment.interfaces";
import {
  useCreateScoringStructureHook,
  useUpdateScoringStructureHook,
} from "../../../hooks/useAssessmentHook";
import { logger } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";
import { useClassHook } from "../../../hooks/useAcademicHook";

const componentSchema = z.object({
  name: z
    .string()
    .min(1, "Component name is required")
    .max(100, "Component name must not exceed 100 characters"),
  weightPercentage: z.coerce
    .number()
    .min(0, "Weight must be at least 0%")
    .max(100, "Weight must not exceed 100%"),
  marks: z.number().min(0, "Marks must be at least 0").optional(),
});

const scoringStructureSchema = z.object({
  classId: z
    .object({
      id: z.string(),
      label: z.string(),
    })
    .refine((val) => val && val.id, {
      message: "Please select a class",
    }),
  totalMarks: z.coerce.number().min(1, "Total marks must be at least 1"),
  passingMarks: z.coerce.number().min(0, "Passing marks must be at least 0"),
  components: z
    .array(componentSchema)
    .min(1, "At least one assessment component is required")
    .refine(
      (components) => {
        const totalWeight = components.reduce(
          (sum, comp) => sum + comp.weightPercentage,
          0,
        );
        return Math.abs(totalWeight - 100) < 0.01; // Allow small floating point differences
      },
      {
        message: "Component weights must total exactly 100%",
      },
    ),
});

type ScoringStructureFormData = z.infer<typeof scoringStructureSchema>;

interface ManageScoringStructureDialogProps {
  open: boolean;
  onClose: () => void;
  scoringStructure?: TScoringStructure | null;
  classId?: string; // Pre-select a class if provided
}

/** Prefer API body message (axios `response.data`) over generic Error.message. */
function getApiErrorMessage(error: unknown): string | undefined {
  if (error == null) {
    return undefined;
  }
  const res = (error as { response?: { data?: unknown } }).response;
  const body = res?.data;
  if (body != null && typeof body === "object" && !Array.isArray(body)) {
    const top = body as Record<string, unknown>;
    if (typeof top.message === "string" && top.message.trim()) {
      return top.message;
    }
    const inner = top.data;
    if (
      inner != null &&
      typeof inner === "object" &&
      !Array.isArray(inner) &&
      typeof (inner as Record<string, unknown>).message === "string"
    ) {
      const nested = (inner as Record<string, unknown>).message as string;
      if (nested.trim()) {
        return nested;
      }
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return undefined;
}

const ManageScoringStructureDialog: FC<ManageScoringStructureDialogProps> = ({
  open,
  onClose,
  scoringStructure,
  classId,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const createStructureMutation = useCreateScoringStructureHook();
  const updateStructureMutation = useUpdateScoringStructureHook();
  const { data: classes } = useClassHook();
  const isEditMode = !!scoringStructure;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ScoringStructureFormData>({
    resolver: zodResolver(scoringStructureSchema),
    defaultValues: {
      classId: { id: "", label: "" },
      totalMarks: 100,
      passingMarks: 40,
      components: [{ name: "", weightPercentage: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "components",
  });

  const watchedComponents = watch("components");
  const totalWeight = useMemo(() => {
    return watchedComponents.reduce((sum, comp) => {
      const w = Number(comp.weightPercentage);
      return sum + (Number.isFinite(w) ? w : 0);
    }, 0);
  }, [watchedComponents]);

  const classOptions = useMemo(() => {
    return (
      classes?.map((cls) => ({
        id: cls.id,
        label: cls.name,
      })) || []
    );
  }, [classes]);

  useEffect(() => {
    if (scoringStructure && classes) {
      const selectedClass = classes.find(
        (cls) => cls.id === scoringStructure.classId,
      );

      reset({
        classId: selectedClass
          ? { id: selectedClass.id, label: selectedClass.name }
          : { id: "", label: "" },
        totalMarks: scoringStructure.totalMarks,
        passingMarks: scoringStructure.passingMarks,
        components: scoringStructure.components.map((comp) => ({
          name: comp.name,
          weightPercentage: comp.weightPercentage,
          marks: comp.marks,
        })),
      });
    } else if (classId && classes) {
      const selectedClass = classes.find((cls) => cls.id === classId);
      reset({
        classId: selectedClass
          ? { id: selectedClass.id, label: selectedClass.name }
          : { id: "", label: "" },
        totalMarks: 100,
        passingMarks: 40,
        components: [{ name: "", weightPercentage: 0 }],
      });
    } else {
      reset({
        classId: { id: "", label: "" },
        totalMarks: 100,
        passingMarks: 40,
        components: [{ name: "", weightPercentage: 0 }],
      });
    }
  }, [scoringStructure, reset, open, classes, classId]);

  const onSubmit = async (data: ScoringStructureFormData) => {
    if (!selectedInstitute) {
      logger.show_error("Please select an institute first");
      return;
    }

    try {
      const components: Omit<TAssessmentComponent, "id">[] =
        data.components.map((comp) => ({
          name: comp.name,
          weightPercentage: comp.weightPercentage,
          marks: comp.marks,
        }));

      if (isEditMode && scoringStructure) {
        await updateStructureMutation.mutateAsync({
          id: scoringStructure.id,
          request: {
            totalMarks: data.totalMarks,
            passingMarks: data.passingMarks,
            components,
          },
        });
        logger.show_success("Scoring structure updated successfully!");
      } else {
        await createStructureMutation.mutateAsync({
          classId: data.classId.id,
          totalMarks: data.totalMarks,
          passingMarks: data.passingMarks,
          components,
          instituteId: selectedInstitute.id,
        });
        logger.show_success("Scoring structure created successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        getApiErrorMessage(error) ||
          `Failed to ${isEditMode ? "update" : "create"} scoring structure. Please try again.`,
      );
    }
  };

  const handleAddComponent = () => {
    append({ name: "", weightPercentage: 0 });
  };

  const handleRemoveComponent = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const isLoading =
    isSubmitting ||
    createStructureMutation.isPending ||
    updateStructureMutation.isPending;
  const isError =
    createStructureMutation.isError || updateStructureMutation.isError;
  const errorMessage = createStructureMutation.isError
    ? getApiErrorMessage(createStructureMutation.error)
    : updateStructureMutation.isError
      ? getApiErrorMessage(updateStructureMutation.error)
      : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-scoring-structure-dialog"
    >
      <DialogTitle data-testid="manage-scoring-structure-dialog-title">
        {isEditMode ? "Edit Scoring Structure" : "Configure Scoring Structure"}
      </DialogTitle>
      <form
        onSubmit={handleSubmit(onSubmit)}
        data-testid="scoring-structure-form"
      >
        <DialogContent data-testid="scoring-structure-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="scoring-structure-form-error"
            >
              {errorMessage ||
                `Failed to ${isEditMode ? "update" : "create"} scoring structure. Please try again.`}
            </Alert>
          )}

          <Grid
            container
            spacing={3}
            sx={{ mt: 1 }}
            data-testid="scoring-structure-form-container"
          >
            <ControlledAutocomplete
              control={control}
              name="classId"
              id="scoring-structure-class"
              label="Class"
              dataOptions={classOptions}
              errorMessage={errors.classId?.message}
              dataTestId="scoring-structure-class-input"
            />

            <ControlledTextField
              control={control}
              name="totalMarks"
              id="scoring-structure-total-marks"
              label="Total Marks"
              type="number"
              required
              errorMessage={errors.totalMarks?.message}
              inputProps={{
                "data-testid": "scoring-structure-total-marks-input",
                min: 1,
              }}
            />

            <ControlledTextField
              control={control}
              name="passingMarks"
              id="scoring-structure-passing-marks"
              label="Passing Marks"
              type="number"
              required
              errorMessage={errors.passingMarks?.message}
              inputProps={{
                "data-testid": "scoring-structure-passing-marks-input",
                min: 0,
              }}
            />

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography
                  variant="h6"
                  data-testid="scoring-structure-components-title"
                >
                  Assessment Components
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddComponent}
                  data-testid="add-scoring-component-button"
                >
                  Add Component
                </Button>
              </Box>

              {fields.map((field, index) => (
                <Box
                  key={field.id}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                  data-testid={`scoring-component-${index}`}
                >
                  <Grid container spacing={2} alignItems="center">
                    <ControlledTextField
                      control={control}
                      name={`components.${index}.name`}
                      id={`component-name-${index}`}
                      label="Component Name"
                      required
                      errorMessage={errors.components?.[index]?.name?.message}
                      inputProps={{
                        "data-testid": `component-name-input-${index}`,
                      }}
                    />
                    <ControlledTextField
                      control={control}
                      name={`components.${index}.weightPercentage`}
                      id={`component-weight-${index}`}
                      label="Weight (%)"
                      type="number"
                      required
                      errorMessage={
                        errors.components?.[index]?.weightPercentage?.message
                      }
                      inputProps={{
                        "data-testid": `component-weight-input-${index}`,
                        min: 0,
                        max: 100,
                        step: 0.01,
                      }}
                      sx={{
                        width: "100%",
                      }}
                    />
                    <Grid item xs={12} sm={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <IconButton
                        onClick={() => handleRemoveComponent(index)}
                        disabled={fields.length === 1}
                        color="error"
                        size="small"
                        data-testid={`remove-component-button-${index}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}

              <Box
                sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}
              >
                <Typography
                  variant="body2"
                  data-testid="scoring-structure-total-weight"
                >
                  Total Weight: <strong>{totalWeight.toFixed(2)}%</strong>
                  {Math.abs(totalWeight - 100) > 0.01 && (
                    <Typography component="span" color="error" sx={{ ml: 1 }}>
                      (Must equal 100%)
                    </Typography>
                  )}
                </Typography>
              </Box>

              {errors.components?.root && (
                <Alert
                  severity="error"
                  sx={{ mt: 2 }}
                  data-testid="scoring-structure-components-error"
                >
                  {errors.components.root.message}
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions data-testid="scoring-structure-dialog-actions">
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-scoring-structure-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-scoring-structure-button"
          >
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update Structure"
                : "Create Structure"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageScoringStructureDialog;
